import React, { useCallback, useEffect, useState } from 'react';
import { ActivityProps, ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { ModalCreateNote } from '@europrocurement/flexy-components/components/organisms/ModalCreateNote';
import { ModalEditNote } from '@europrocurement/flexy-components/components/organisms/ModalEditNote';
import { useTiersService, useSociete } from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import { Box, useTheme } from '@mui/material';
import { GetNotesContactContactsItem200ResponseNotesInner } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { IaButton } from '@europrocurement/l2d-modules/modules/IaServices';
import { useResolveUserByKeycloakId } from '../../../hooks';
import IAMenu from '../../IaServices/components/IAMenu/IAMenu';
import {
    SET_CURRENT_PUBLISHER_NOTES_CTX,
    useCurrentPublisherCtx,
    useCurrentPublisherCtxDispatch,
    useCurrentPublisherActions,
} from '../providers/CurrentPublisherProvider';

const NOTE_DELETE_MESSAGES = {
    question: 'Supprimer cette note ?',
    detail: {
        message: 'Cette action est irréversible. La note sera définitivement supprimée.',
        severity: 'warning' as const,
    },
    validation: 'Supprimer',
    annulation: 'Annuler',
};

type UsePublisherActivitiesHookProps = {
    actionOnSuccess?: () => void;
    defaultMemoClient?: boolean;
    useMemoClient?: boolean;
};

const usePublisherActivitiesHook = ({
    actionOnSuccess = undefined,
    useMemoClient = true,
    defaultMemoClient = false,
}: UsePublisherActivitiesHookProps) => {
    const [isEditingSaving, setIsEditingSaving] = useState<boolean>(false);
    const [isDeletingSaving, setIsDeletingSaving] = useState<boolean>(false);
    const [activities, setActivities] = useState<ActivityProps[]>([]);
    const [activitiesLoading, setActivitiesLoading] = useState(true);

    const { modalActions } = useModal();
    const { publisher, notesFetchFailed, notesFetchAttempt, isFetchingNotes } =
        useCurrentPublisherCtx();
    const dispatchCurrentPublisher = useCurrentPublisherCtxDispatch();
    const publisherActions = useCurrentPublisherActions();

    const { publishersApi } = useTiersService();
    const xIdSociete = useSociete();
    const { enqueueSnackbar } = useSnackbar();
    const { palette } = useTheme();
    const { resolveUserByKeycloakId } = useResolveUserByKeycloakId();

    const renderIAMenu = (
        content: string,
        setContent: (value: React.SetStateAction<string>) => void,
    ) => (
        <Box sx={{ position: 'relative', zIndex: 2 }}>
            <IAMenu
                sxProps={{
                    position: 'absolute',
                    right: '5px',
                    top: '18px',
                }}
            >
                <IaButton
                    getParams={() => ({
                        textContent: content,
                    })}
                    onSuccess={(data) => {
                        const correctedText = data.answer;
                        if (correctedText) {
                            setContent(correctedText);
                            enqueueSnackbar('Correction effectuée');
                        }
                    }}
                />
                <IaButton
                    applicationName="L2D_Markdown"
                    label="Mettre en page avec Markdown"
                    getParams={() => ({
                        textContent: content,
                    })}
                    onSuccess={(data) => {
                        const correctedText = data.answer;
                        if (correctedText) {
                            setContent(correctedText);
                            enqueueSnackbar('Mise en page effectuée');
                        }
                    }}
                />
            </IAMenu>
        </Box>
    );

    const formatUserName = (
        prenom?: string,
        nom?: string,
    ): { firstName: string; lastName: string } => {
        if (!prenom || !nom) {
            return { firstName: '', lastName: '' };
        }

        return {
            firstName: prenom.charAt(0).toUpperCase() + prenom.slice(1).toLowerCase(),
            lastName: nom.toUpperCase(),
        };
    };

    const createNoteActivity = useCallback(
        async (note: GetNotesContactContactsItem200ResponseNotesInner): Promise<ActivityProps> => {
            const createdByUser = note.createdBy
                ? await resolveUserByKeycloakId(note.createdBy)
                : null;
            const updatedByUser = note.updatedBy
                ? await resolveUserByKeycloakId(note.updatedBy)
                : null;

            const createdByFormatted = createdByUser
                ? formatUserName(createdByUser?.firstName ?? '', createdByUser?.lastName ?? '')
                : { firstName: note.createdBy || 'Anonyme', lastName: '' };

            const updatedByFormatted = updatedByUser
                ? formatUserName(updatedByUser.firstName ?? '', updatedByUser.lastName ?? '')
                : { firstName: note.updatedBy || '', lastName: '' };

            const userId = createdByUser?.keycloakUserIdentifier || note.createdBy || 'unknown';

            return {
                id: `note-${note.id}`,
                type: 'note',
                noteType: note.highlighted ? 'important' : 'standard',
                content: note.content || '',
                createdAt: note.createdAt ? new Date(note.createdAt) : new Date(),
                userId,
                userFirstName: createdByFormatted.firstName,
                userLastName: createdByFormatted.lastName,
                updatedAt: note.updatedAt ? new Date(note.updatedAt) : undefined,
                updatedBy: note.updatedBy || undefined,
                updatedByFirstName: updatedByFormatted.firstName,
                updatedByLastName: updatedByFormatted.lastName,
                memoClient: (note as { memoClient?: boolean }).memoClient || false,
            };
        },
        [resolveUserByKeycloakId],
    );

    const addActivities = useCallback(async () => {
        const noteActivities = await Promise.all((publisher?.notes || []).map(createNoteActivity));

        const allActivities = [...noteActivities].sort(
            (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
        );

        return allActivities;
    }, [createNoteActivity, publisher?.notes]);

    const loadAllActivities = useCallback(async () => {
        if (!publisher?.id) return;

        try {
            const allActivities = await addActivities();
            setActivities(allActivities);
        } catch (error) {
            console.error('Erreur lors du chargement des activités:', error);
        } finally {
            setActivitiesLoading(false);
        }
    }, [publisher?.id, addActivities]);

    useEffect(() => {
        loadAllActivities();
    }, [loadAllActivities, publisher?.notes]);

    useEffect(() => {
        if (notesFetchFailed) {
            setActivitiesLoading(false);
        }
    }, [notesFetchFailed]);

    useEffect(() => {
        if (isFetchingNotes) {
            setActivitiesLoading(true);
        }
    }, [isFetchingNotes]);

    const handleAddNote = () => {
        modalActions.call(
            <ModalCreateNote
                renderIAMenu={renderIAMenu}
                defaultMemoClient={defaultMemoClient}
                onConfirm={async (content: string, highlighted: boolean, memoClient: boolean) => {
                    if (!publisher?.id) return;

                    try {
                        const newNote = await publishersApi.createNoteFournisseurFournisseurItem({
                            id: publisher.id,
                            createNoteFournisseurFournisseurItemRequest: {
                                content,
                                highlighted,
                                memoClient,
                            },
                            xIdSociete,
                        });

                        if (
                            typeof dispatchCurrentPublisher !== 'undefined' &&
                            dispatchCurrentPublisher !== null
                        ) {
                            dispatchCurrentPublisher({
                                type: SET_CURRENT_PUBLISHER_NOTES_CTX,
                                payload: [...(publisher.notes || []), newNote.data],
                            });
                        }

                        enqueueSnackbar(`Note créée avec succès`);
                        if (typeof actionOnSuccess !== 'undefined') {
                            actionOnSuccess();
                        } else {
                            modalActions.close();
                        }
                    } catch (error: unknown) {
                        console.error('Erreur lors de la création de la note:', error);
                        enqueueSnackbar(`Erreur lors de la création de la note`, {
                            variant: 'error',
                        });
                    }
                }}
                useMemoClient={useMemoClient}
            />,
        );
    };

    const handleEditNote = (
        noteId: string,
        content: string,
        highlighted: boolean,
        memoClient: boolean,
    ) => {
        modalActions.call(
            <ModalEditNote
                initialContent={content}
                initialHighlighted={highlighted}
                initialMemoClient={memoClient}
                useMemoClient
                isSaving={isEditingSaving}
                renderIAMenu={renderIAMenu}
                onConfirm={async (
                    newContent: string,
                    newHighlighted: boolean,
                    newMemoClient: boolean,
                ) => {
                    if (!publisher?.id) return;

                    try {
                        setIsEditingSaving(true);

                        const noteIdNum = +noteId.replace('note-', '');

                        const editedNote = await publishersApi.updateNoteFournisseurFournisseurItem(
                            {
                                id: publisher.id,
                                noteId: noteIdNum,
                                updateNoteFournisseurFournisseurItemRequest: {
                                    content: newContent,
                                    highlighted: newHighlighted,
                                    memoClient: newMemoClient,
                                },
                                xIdSociete,
                            },
                        );

                        if (
                            typeof dispatchCurrentPublisher !== 'undefined' &&
                            dispatchCurrentPublisher !== null
                        ) {
                            dispatchCurrentPublisher({
                                type: SET_CURRENT_PUBLISHER_NOTES_CTX,
                                payload: (publisher.notes || []).map((note) => {
                                    if (note.id === editedNote.data.id) {
                                        return editedNote.data;
                                    }
                                    return note;
                                }),
                            });
                        }
                        enqueueSnackbar(`Note mise à jour avec succès`);
                        if (typeof actionOnSuccess !== 'undefined') {
                            actionOnSuccess();
                        } else {
                            modalActions.close();
                        }
                    } catch (error) {
                        console.error('Erreur lors de la modification de la note:', error);

                        const isUnauthorized =
                            (error &&
                                typeof error === 'object' &&
                                'status' in error &&
                                error.status === 403) ||
                            (error &&
                                typeof error === 'object' &&
                                'response' in error &&
                                error.response &&
                                typeof error.response === 'object' &&
                                'status' in error.response &&
                                error.response.status === 403);

                        if (isUnauthorized) {
                            enqueueSnackbar(`Vous n'avez pas les droits pour modifier cette note`, {
                                variant: 'error',
                            });
                        } else {
                            enqueueSnackbar(`Erreur lors de la modification de la note`, {
                                variant: 'error',
                            });
                        }
                    } finally {
                        setIsEditingSaving(false);
                    }
                }}
                onCancel={() => modalActions.close()}
            />,
        );
    };

    const handleDeleteNote = (noteIdStr: string) => {
        modalActions.call(
            <ConfirmationModal
                messages={NOTE_DELETE_MESSAGES}
                color={palette.error}
                disabled={isDeletingSaving}
                actionOnValidation={async () => {
                    if (!publisher?.id) return;

                    try {
                        setIsDeletingSaving(true);
                        const noteId = +noteIdStr.replace('note-', '');
                        await publishersApi.deleteNoteFournisseurFournisseurItem({
                            id: publisher.id,
                            noteId,
                            xIdSociete,
                        });

                        if (
                            typeof dispatchCurrentPublisher !== 'undefined' &&
                            dispatchCurrentPublisher !== null
                        ) {
                            dispatchCurrentPublisher({
                                type: SET_CURRENT_PUBLISHER_NOTES_CTX,
                                payload: (publisher.notes || []).filter(
                                    (note) => note.id !== noteId,
                                ),
                            });
                        }

                        enqueueSnackbar(`Note supprimée avec succès`);

                        if (typeof actionOnSuccess !== 'undefined') {
                            actionOnSuccess();
                        } else {
                            modalActions.close();
                        }
                    } catch (error) {
                        console.error('Erreur lors de la suppression de la note:', error);

                        const isUnauthorized =
                            (error &&
                                typeof error === 'object' &&
                                'status' in error &&
                                error.status === 403) ||
                            (error &&
                                typeof error === 'object' &&
                                'response' in error &&
                                error.response &&
                                typeof error.response === 'object' &&
                                'status' in error.response &&
                                error.response.status === 403);

                        if (isUnauthorized) {
                            enqueueSnackbar(
                                `Vous n'avez pas les droits pour supprimer cette note`,
                                {
                                    variant: 'error',
                                },
                            );
                        } else {
                            enqueueSnackbar(`Erreur lors de la suppression de la note`, {
                                variant: 'error',
                            });
                        }
                    } finally {
                        setIsDeletingSaving(false);
                    }
                }}
                actionOnCancellation={() => modalActions.close()}
            />,
        );
    };

    return {
        activities,
        activitiesLoading,
        notesFetchFailed,
        notesFetchAttempt,
        isFetchingNotes,
        handleAddNote,
        handleEditNote,
        handleDeleteNote,
        handleRetryNotes: publisherActions?.retryNotes ?? (() => {}),
    };
};

export default usePublisherActivitiesHook;
